/**
 * HRCommandCenter — /hr module shell.
 *
 * Four tabs (Workforce Overview default, Clinic Staffing, Hiring Pipeline
 * placeholder, Retention & Attrition placeholder) + Settings popover.
 *
 * Payroll and Clinician Roster live at their existing routes; this page
 * only owns the four analytical tabs.
 */
import React from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Users2, Building2, Briefcase, LineChart as LineChartIcon, TrendingUp, ClipboardEdit, Inbox, BarChart3 } from 'lucide-react';
import WorkforceOverview from '../components/hr/WorkforceOverview';
import ClinicStaffing from '../components/hr/ClinicStaffing';
import HiringPipeline from '../components/hr/HiringPipeline';
import HiringTracker from '../components/hr/HiringTracker';
import RetentionAttrition from '../components/hr/RetentionAttrition';
import IntakeLog from '../components/hr/IntakeLog';
import HiringMetrics from '../components/hr/HiringMetrics';
import HRSettingsPopover from '../components/hr/HRSettingsPopover';

const C = {
  ink: '#2D2833', ink2: '#685B72', soft: '#8A7D95',
  border: '#E8E5EC', paper: '#FBF8F4', plum: '#685B72',
  gold: '#B08A3A',
};

const TABS = [
  { key: 'overview',  label: 'CEO Staffing Overview',  icon: Users2 },
  { key: 'staffing',  label: 'Clinic Staffing',        icon: Building2 },
  { key: 'intake',    label: 'Intake Log',             icon: Inbox },
  { key: 'pipeline',  label: 'Hiring Pipeline',        icon: Briefcase },
  { key: 'hiring',    label: 'Hiring Tracker',         icon: TrendingUp },
  { key: 'metrics',   label: 'Hiring Metrics',         icon: BarChart3 },
  { key: 'retention', label: 'Retention & Attrition',  icon: LineChartIcon },
];

function ComingSoonCard({ title, blurb, testId }) {
  return (
    <Card className="p-8 border text-center" style={{ borderColor: C.border }} data-testid={testId}>
      <div className="text-[11px] uppercase tracking-widest font-bold mb-2" style={{ color: C.gold }}>Stage 2 · Coming soon</div>
      <div className="font-serif text-xl mb-2" style={{ color: C.ink }}>{title}</div>
      <div className="text-sm max-w-md mx-auto" style={{ color: C.soft }}>{blurb}</div>
    </Card>
  );
}

export default function HRCommandCenter() {
  const { tab } = useParams();
  const navigate = useNavigate();
  const activeKey = TABS.find(t => t.key === tab) ? tab : 'overview';

  const renderTab = () => {
    switch (activeKey) {
      case 'overview':  return <WorkforceOverview />;
      case 'staffing':  return <ClinicStaffing />;
      case 'intake':    return <IntakeLog />;
      case 'pipeline':  return <HiringPipeline />;
      case 'hiring':    return <HiringTracker />;
      case 'metrics':   return <HiringMetrics />;
      case 'retention': return <RetentionAttrition />;
      default: return null;
    }
  };

  return (
    <div className="space-y-4" data-testid="hr-command-center">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] font-bold" style={{ color: C.plum }}>HR Command Center</div>
          <div className="font-serif text-3xl" style={{ color: C.ink }}>People operations, at a glance</div>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <Button asChild variant="outline" size="sm" className="gap-2" data-testid="hr-open-roster">
            <Link to="/clinicians">
              <ClipboardEdit size={14} /> Clinician Roster
            </Link>
          </Button>
          <HRSettingsPopover onSaved={() => window.dispatchEvent(new Event('hr:settings-changed'))} />
        </div>
      </div>

      {/* Tab bar */}
      <div className="border-b flex gap-1" style={{ borderColor: C.border }} data-testid="hr-tab-bar">
        {TABS.map(t => {
          const Icon = t.icon;
          const active = t.key === activeKey;
          return (
            <button
              key={t.key}
              onClick={() => navigate(`/hr/${t.key}`)}
              data-testid={`hr-tab-${t.key}`}
              className="px-4 py-2.5 flex items-center gap-2 text-sm border-b-2 transition-colors"
              style={{
                borderColor: active ? C.gold : 'transparent',
                color: active ? C.ink : C.ink2,
                fontWeight: active ? 600 : 400,
              }}
            >
              <Icon size={14} strokeWidth={1.75} /> {t.label}
            </button>
          );
        })}
      </div>

      {renderTab()}
    </div>
  );
}
